/**
 * Phase 8.6 — Concierge Event Home shell.
 *
 * Calm, focused Act Two surface for Concierge events in early lifecycle.
 * Rendered conditionally at /events/:eventId when:
 *   1. REACT_APP_ENABLE_EVENT_HOME === "true", AND
 *   2. the Operations projection returns is_pre_graduation === true.
 *
 * Composes existing Phase 8e Operations sections — no new design tokens,
 * no new section components, no new chrome. The Event Home is a
 * presentation layer over the existing projection, not a new product.
 *
 * Strict EDD-aligned exclusions:
 *   * no payments / bookings / vendor tracker / marketplace search
 *   * no empty hero cards
 *   * no customer-facing bridge action
 *   * no admin language, worker IDs, scores, confidence, audit terms,
 *     internal lifecycle labels
 *
 * Layout (mobile-first):
 *   1. Event acknowledgment (event_type · date · location · guests)
 *   2. Concierge presence band (single calm sentence — "what's
 *      happening for you right now")
 *   3. Brief Rail gateway (when the brief is sealed)
 *   4. Categories being prepared (optional, when sealed brief absent)
 *   5. Outreach narrative — bridge timeline customer view (typically
 *      empty during pre-graduation; defense-in-depth render anyway)
 *   6. Concierge signature footer
 *
 * Failure modes: any axios error → silent hide (the parent EventDetail
 * routing decision then falls back to the existing layout via its own
 * conditional).
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Heart, Mail, ArrowRight, ArrowLeft } from 'lucide-react';

import EventOverviewSection from './sections/EventOverviewSection';
import BriefRailSection from './sections/BriefRailSection';
import BridgeTimelineSection from './sections/BridgeTimelineSection';
import ServiceProgressSection from './sections/ServiceProgressSection';
import { isConciergeManualMode } from './manualModeFlag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calm presence-band copy. The keys map to existing `lifecycle_stage`
// values produced by Phase 8a's derivation; the strings are the
// customer-facing surface and contain no internal vocabulary.
//
// Phase A — Manual Mode rewrite (ADR-CONCIERGE-PHASE-A):
//   * No mention of "Brief", "shortlist", "vendors", or implied
//     automated outreach. Manual Mode is the production reality —
//     the team replies by email; the in-app surface is a calm
//     acknowledgment only.
//   * Lifecycle keys are preserved (no enum change). Stages that
//     should not appear pre-graduation collapse to the same calm
//     message defensively.
const PRESENCE_COPY = {
  intake: {
    headline: 'We received your event',
    body: "Your Concierge is reading through what you shared. We'll reach out by email — usually within one to two business days.",
  },
  concierge_active: {
    headline: 'Your Concierge is on it',
    body: "We're working on your event behind the scenes. The next update will come to your inbox.",
  },
  brief_ready: {
    headline: 'Your Concierge is on it',
    body: "We're working on your event behind the scenes. The next update will come to your inbox.",
  },
  vendors_engaged: {
    headline: 'Your Concierge is on it',
    body: "We're working on your event behind the scenes. The next update will come to your inbox.",
  },
};

const DEFAULT_PRESENCE = {
  headline: 'Your Concierge is on it',
  body: "We're working on your event. The next update will come to your inbox.",
};

export default function EventHome({ eventId }) {
  const navigate = useNavigate();
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);

  const fetchProjection = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/events/${eventId}/operations`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setProjection(res.data || null);
      setAvailable(true);
    } catch (_err) {
      setProjection(null);
      setAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchProjection();
  }, [fetchProjection]);

  if (loading && !projection) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-violet-50/40 via-white to-white"
        data-testid="event-home-loading"
      >
        <div className="max-w-2xl mx-auto px-5 pt-12 pb-24">
          <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mb-4" />
          <div className="h-8 w-3/4 bg-gray-100 rounded animate-pulse mb-3" />
          <div className="h-3 w-full bg-gray-50 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!available || !projection) return null;

  // Phase A — Manual Mode gate (ADR-CONCIERGE-PHASE-A). Read once per
  // render; defaults to TRUE so misconfiguration cannot accidentally
  // expose Hybrid-Mode surfaces.
  const manualMode = isConciergeManualMode();

  const stage = projection.lifecycle_stage;
  const presence = PRESENCE_COPY[stage] || DEFAULT_PRESENCE;
  const rail = projection.brief_rail || {};
  const showBriefRail = !manualMode && !!rail.has_brief && rail.latest_status === 'ready';
  const showCategoriesBeingPrepared = !manualMode
    && !showBriefRail
    && Array.isArray(projection.service_progress)
    && projection.service_progress.length > 0;
  // Bridge timeline customer surface — typically empty pre-graduation
  // (graduation happens when the first created row appears). Render
  // defensively for the edge case where a stale projection arrives.
  // In Manual Mode the timeline is suppressed upstream regardless.
  const timeline = manualMode ? [] : (projection.bridge_timeline || []);

  // B-EXP-6.20 — Authenticated Concierge Brief Continuity.
  // When the customer's Concierge Brief has been published, EventHome
  // MUST evolve out of the calm "on it" holding state into the "ready"
  // state that connects them to the SAME canonical brief renderer that
  // the staff-shared customer link uses. The share_token is stable
  // across republishes (see manual_brief_bridge.seal_and_publish), so
  // the CTA target does not drift between revisions.
  const manualBrief = projection.manual_brief || {};
  const briefReady = manualMode && !!manualBrief.published && !!manualBrief.share_token;
  const briefHref = briefReady
    ? (manualBrief.share_path || `/api/concierge/brief/${encodeURIComponent(manualBrief.share_token)}`) + '?from=events'
    : null;

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-violet-50/40 via-white to-white"
      data-testid="event-home"
    >
      <div className="max-w-2xl mx-auto px-5 pt-10 pb-24 space-y-6">
        {/* Escape hatch — authenticated in-app surface must always offer a
            way back to the customer's Events list (the anonymous secure
            brief link never renders this component). */}
        <button
          type="button"
          onClick={() => navigate('/customer-dashboard?tab=events')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="event-home-back-to-events"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          My Events
        </button>

        {/* Zone 1 — Event acknowledgment */}
        <EventOverviewSection event={projection.event} />

        {/* Zone 2 — Concierge presence band.
            B-EXP-6.20 — When a Concierge Brief has been published, the
            calm "on it" holding copy is replaced by a ready-state card
            with a CTA into the canonical brief renderer. The share
            token is stable across republishes; the CTA target does not
            drift between revisions. */}
        {briefReady ? (
          <div
            className="rounded-2xl bg-white border border-violet-200 shadow-sm p-5"
            data-testid="event-home-brief-ready"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-500" aria-hidden />
              <p className="text-xs uppercase tracking-wide text-violet-500">
                Your Concierge
              </p>
            </div>
            <p
              className="text-base font-semibold text-gray-900 leading-snug"
              data-testid="event-home-brief-ready-headline"
            >
              Your Concierge Brief is ready
            </p>
            <p
              className="text-sm text-gray-600 mt-1.5 leading-relaxed"
              data-testid="event-home-brief-ready-body"
            >
              We found recommendations for your event. Review your options,
              tell us what you like, and leave notes for your Concierge.
            </p>
            <a
              href={briefHref}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium px-4 py-2.5 transition-colors"
              data-testid="event-home-brief-ready-cta"
              data-share-token={manualBrief.share_token}
            >
              Review my recommendations
              <ArrowRight className="w-4 h-4" aria-hidden />
            </a>
          </div>
        ) : (
          <div
            className="rounded-2xl bg-white border border-violet-100 p-5"
            data-testid="event-home-presence-band"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-500" aria-hidden />
              <p className="text-xs uppercase tracking-wide text-violet-500">
                Your Concierge
              </p>
            </div>
            <p
              className="text-base font-semibold text-gray-900 leading-snug"
              data-testid="event-home-presence-headline"
            >
              {presence.headline}
            </p>
            <p
              className="text-sm text-gray-600 mt-1.5 leading-relaxed"
              data-testid="event-home-presence-body"
            >
              {presence.body}
            </p>
          </div>
        )}

        {/* Zone 3 — Brief Rail gateway (only when sealed brief exists; Manual Mode hides) */}
        {showBriefRail && <BriefRailSection rail={rail} eventId={eventId} />}

        {/* Zone 4 — Categories being prepared (only pre-Brief; Manual Mode hides) */}
        {showCategoriesBeingPrepared && (
          <div data-testid="event-home-categories-being-prepared">
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
              We&apos;re looking at
            </p>
            <ServiceProgressSection items={projection.service_progress} />
          </div>
        )}

        {/* Zone 5 — Outreach narrative (defensive — usually empty here; Manual Mode hides) */}
        {timeline.length > 0 && <BridgeTimelineSection timeline={timeline} />}

        {/* Zone 5b — Manual Mode email guidance (customer expectation setter).
            B-EXP-6.20 — Hidden when the ready-state card is showing: the
            CTA already tells the customer what to do next and the email
            guidance would compete for attention. */}
        {manualMode && !briefReady && (
          <div
            className="rounded-2xl bg-violet-50/60 border border-violet-100 p-4 flex items-start gap-3"
            data-testid="event-home-email-guidance"
          >
            <Mail className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" aria-hidden />
            <div>
              <p className="text-sm font-medium text-gray-900 leading-snug">
                We&apos;ll be in touch by email
              </p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Please keep an eye on your inbox — and check your spam folder just in case. If you need to reach us in the meantime, reply to any email from your Concierge.
              </p>
            </div>
          </div>
        )}

        {/* Zone 6 — Concierge signature footer */}
        <div
          className="pt-4 text-center"
          data-testid="event-home-signature"
        >
          <p className="text-xs text-gray-400 inline-flex items-center gap-1.5">
            <Heart className="w-3 h-3 text-violet-300" aria-hidden />
            We&apos;re nearby — your Concierge is working on this.
          </p>
        </div>
      </div>
    </div>
  );
}
